import React, { useEffect, useState } from 'react';
import RangeSlider from '../../atoms/RangeSlider';
import { usePathname, useSearchParams } from 'next/navigation';
import { FEATURE } from '../../../constants/emarsys/feature';
import { useRouter } from '../../../hooks/useCustomRouter';
import BodyMedium from '../../atoms/Text/BodyMedium';
import convertMoney from '../../../helpers/utils/convertMoney';
import eventClickNCL from '../../../helpers/emarsys/new-car-list/eventClickNCL';
import { useDispatch } from 'react-redux';
import { BUTTON_NAME_NEW_CAR_LIST } from '../../../constants/emarsys/new-car-list';
import { formatNumberCurrencyWithoutRP } from '../../../helpers/utils/formatNumberCurrency';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import {
  BUTTON_NAME_TEST_DRIVE_LIST,
  SECTION_NAME_TEST_DRIVE_LIST,
} from '../../../constants/emarsys/test-drive';
import eventClickTestDriveList from '../../../helpers/emarsys/test-drive/eventClickTestDriveList';

export interface FilterPriceRangeProps {
  minPrice: { value: number };
  maxPrice: { value: number };
  slideMin?: number;
  slideMax?: number;
  typeID?: string;
  feature?: string;
  impressionData?: {
    title?: string;
    category?: string;
    customerNo?: string;
    carCategory?: string;
    carModel?: string;
    sectionName?: string;
    filterValue?: {
      price?: string;
      search?: string;
      color?: string;
      fuel_type?: string;
      transmission?: string;
      capacity?: string;
      tag?: string;
      order_by?: string;
    };
    menu_cat_lvl1?: string;
    menu_cat_lvl2?: string;
  };
  breadcrumbItems?: IBreadcrumbs[];
}

export default function FilterPriceRange({
  minPrice,
  maxPrice,
  slideMin,
  slideMax,
  typeID,
  feature,
  impressionData,
  breadcrumbItems,
}: FilterPriceRangeProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const initialMin = slideMin ? slideMin : 0;
  const initialMax = slideMax ? slideMax : maxPrice?.value || 0;
  const defaultValue = [initialMin, initialMax];
  const max = maxPrice?.value || 0;
  const [minValue, setMinValue] = useState(initialMin);
  const [maxValue, setMaxValue] = useState(initialMax);

  useEffect(() => {
    if (!max) return;
    if (!router.query?.priceFilter) {
      setMaxValue(initialMax > max ? max : initialMax);
      setMinValue(initialMin);
    }
  }, [router.query.priceFilter, max]);

  useEffect(() => {
    // update maxValue when max changed to prevent bug from previous value
    if (maxValue > max) {
      setMaxValue(max);
    }
    const priceFilter = searchParams?.get('priceFilter');
    if (priceFilter) {
      const maxPriceFilter = parseInt(priceFilter.split('TO')?.[1]);
      if (maxPriceFilter < max && maxPriceFilter > maxValue) setMaxValue(maxPriceFilter);
    }
    // if (maxValue < max) setMaxValue(max);
  }, [max]);

  const handleOnMouseUp = (min, max) => {
    const existingQueryParams = searchParams.get('q');

    if (feature === FEATURE.LISTING_NEW_CAR) {
      eventClickNCL(
        dispatch,
        impressionData?.customerNo || '',
        impressionData?.title || '',
        impressionData?.carCategory || '',
        impressionData?.carModel || '',
        impressionData?.sectionName || '',
        BUTTON_NAME_NEW_CAR_LIST.KISARAN_HARGA,
        breadcrumbItems,
        {
          ...impressionData.filterValue,
          price: `Rp${formatNumberCurrencyWithoutRP(min)} to Rp${formatNumberCurrencyWithoutRP(max)}`,
        }
      );
    }
    if (feature === FEATURE.TEST_DRIVE_LISTING_PAGE) {
      eventClickTestDriveList(
        impressionData?.title,
        impressionData?.category,
        SECTION_NAME_TEST_DRIVE_LIST.FILTER,
        BUTTON_NAME_TEST_DRIVE_LIST.KISARAN_HARGA,
        impressionData?.menu_cat_lvl1,
        {
          filterPrice: `Rp. ${formatNumberCurrencyWithoutRP(min)} to Rp. ${formatNumberCurrencyWithoutRP(max)}`,
          menu_cat_lvl2: impressionData?.menu_cat_lvl2,
        }
      );
    }

    if (!existingQueryParams) {
      router.push(
        typeID === 'accessories'
          ? `${process.env.NEXT_PUBLIC_URL}${pathname}?priceFilter=${min}+TO+${max}&defaultMin=${minPrice.value}&defaultMax=${maxPrice.value}`
          : pathname +
              `?priceFilter=${min}+TO+${max}&defaultMin=${minPrice.value}&defaultMax=${maxPrice.value}`
      );
    } else {
      router.push(
        typeID === 'accessories'
          ? `${process.env.NEXT_PUBLIC_URL}${pathname}?q=${existingQueryParams}&priceFilter=${min}+TO+${max}&defaultMin=${minPrice.value}&defaultMax=${maxPrice.value}`
          : pathname +
              `?q=` +
              existingQueryParams +
              `&priceFilter=${min}+TO+${max}&defaultMin=${minPrice.value}&defaultMax=${maxPrice.value}`
      );
    }
  };

  function getPriceValue() {
    if (!maxPrice?.value) return 0;

    if (maxPrice.value < 1000000000) {
      return maxPrice.value < 100000000 ? maxPrice.value / 2 : 10000000;
    }

    return 100000000;
  }

  return (
    <div className="flex flex-col w-full rounded-lg py-4">
      <div className="flex justify-between items-center mb-4">
        <BodyMedium>{convertMoney(minValue)}</BodyMedium>

        <BodyMedium>{convertMoney(maxValue)}</BodyMedium>
      </div>
      <div className="mb-4 px-2">
        <RangeSlider
          min={0}
          max={Number(maxPrice?.value || 0)}
          defaultValue={defaultValue}
          onChangeComplete={(data) => {
            const [min, max] = data as number[];
            handleOnMouseUp(min, max);
          }}
          allowCross={false}
          pushable={getPriceValue()}
          value={[minValue, maxValue]}
          onChange={(data) => {
            const [min, max] = data as number[];

            setMinValue(!min || min < 0 ? 0 : min);
            setMaxValue(max);
          }}
        />
      </div>
    </div>
  );
}
